import React, {
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useState,
} from 'react';
import { Helmet } from 'react-helmet';
import { ObservationLoadingAndFailureIndicators } from 'apps/sigma2-ui/src/components/ObservationLoadingAndFailureIndicators';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { documentTitle } from 'apps/sigma2-ui/src/lib/document-head';
import { useAuthenticatedPageRedirect } from 'apps/sigma2-ui/src/lib/hooks/useAuthenticatedPageRedirect';
import { useIsLoadingObservation } from 'apps/sigma2-ui/src/lib/hooks/useIsLoadingObservation';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { MainLayout } from 'apps/sigma2-ui/src/components/MainLayout';
import { isEntitledToRubricsManage } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToRubricsRead } from 'libs/end-to-end/src/group-entitlements';
import { isRubricsPageEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { ListRubricsView } from 'apps/sigma2-ui/src/components/rubrics/views/ListRubricsView';
import { ListRubricsViewItemProps } from 'apps/sigma2-ui/src/components/rubrics/views/ListRubricsView';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { RubricListItemDto } from 'libs/end-to-end/src/rubrics';
import { z } from 'zod';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useUserObservationOrgAndDbIds } from 'apps/sigma2-ui/src/lib/hooks/useUserObservationOrgAndDbIds';
import { PageSection } from 'apps/sigma2-ui/src/components/PageSection';
import { RubricPendingAction } from 'apps/sigma2-ui/src/components/rubrics/RubricList';
import { RubricPendingActionType } from 'apps/sigma2-ui/src/components/rubrics/RubricList';
import { RubricToastData } from 'apps/sigma2-ui/src/components/rubrics/RubricList';
import { RubricStatus } from 'libs/end-to-end/src/types/rubrics/rubric';
import { clientUpdateRubricStatus } from 'apps/sigma2-ui/src/components/rubrics/rubric-utils';
import { clientDeleteRubric } from 'apps/sigma2-ui/src/components/rubrics/rubric-utils';
import { useAlive } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { useUser } from 'apps/sigma2-ui/src/state/user';

const makeToast = ({
  type,
  action,
  name,
}: {
  type: 'success' | 'error';
  action: RubricPendingActionType;
  name: string;
}): RubricToastData => ({
  id: `${type}-${Date.now()}-${Math.random()}`,
  type,
  action,
  name,
});

export const ListRubricsPage: React.FC = () => {
  const { myObservation } = useObservation();
  const { userAuthenticatedApiCall } = useUser();
  const language = useLanguage();
  const navigate = useNavigateSafe();

  useAuthenticatedPageRedirect();

  const {
    isLoadingObservation: isLoadingObservationStill,
    failedToLoadObservation,
  } = useIsLoadingObservation();

  const hasRubricAccess = useMemo(() => {
    if (isLoadingObservationStill || !myObservation) {
      return false;
    }

    const entitled = isEntitledToRubricsRead({
      features: myObservation.features,
    });
    if (!entitled) {
      return false;
    }

    return isRubricsPageEnabledForRevision({ revisionFlags: myObservation });
  }, [isLoadingObservationStill, myObservation]);

  const canManageRubrics = useMemo(() => {
    if (isLoadingObservationStill || !myObservation) {
      return false;
    }

    if (!isRubricsPageEnabledForRevision({ revisionFlags: myObservation })) {
      return false;
    }

    return isEntitledToRubricsManage({
      features: myObservation.features,
    });
  }, [isLoadingObservationStill, myObservation]);

  const {
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
  } = useUserObservationOrgAndDbIds();

  useEffect(() => {
    if (isLoadingObservationStill || !myObservation) {
      return;
    }

    if (hasRubricAccess === false) {
      navigate('/overview');
    }
  }, [hasRubricAccess, isLoadingObservationStill, myObservation, navigate]);

  const getUrlFn = useCallback(() => {
    if (isLoadingObservationStill || !myObservation) {
      return '';
    }

    return `${getApiBaseUrl()}/organizations/${selectedOrganizationId}/databases/${selectedDatabaseId}/revisions/${selectedDatabaseRevisionId}/rubrics`;
  }, [
    isLoadingObservationStill,
    myObservation,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
    selectedOrganizationId,
  ]);

  const listRubricsPattern = useSimpleReadPattern({
    getUrlFn,
    canRead: hasRubricAccess,
    responseSchema: z.array(RubricListItemDto),
  });

  const isLoadingRubrics = useMemo(() => {
    return listRubricsPattern.status === 'loading';
  }, [listRubricsPattern]);

  const isFailedLoadingRubrics = useMemo(() => {
    return listRubricsPattern.status === 'error';
  }, [listRubricsPattern]);

  const isLoadedRef = useRef(false);
  const [list, setList] = useState<ListRubricsViewItemProps[]>([]);

  useEffect(() => {
    if (listRubricsPattern.status === 'done' && !isLoadedRef.current) {
      isLoadedRef.current = true;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setList(
        listRubricsPattern.entity.map((e) => {
          return {
            jobTitles: e.roles,
            name: e.name,
            status: e.status,
            type: e.gradingType,
            id: e.id,
          };
        }),
      );
    }
  }, [listRubricsPattern]);

  const yScrollRef = useRef<HTMLDivElement>(null);

  const [archivingRubric, setArchivingRubric] = useState<string | null>(null);
  const [unarchivingRubric, setUnarchivingRubric] = useState<string | null>(
    null,
  );
  const [deletingRubric, setDeletingRubric] = useState<string | null>(null);
  const [publishingRubric, setPublishingRubric] = useState<string | null>(null);
  const [unpublishingRubric, setUnpublishingRubric] = useState<string | null>(
    null,
  );
  const [pendingAction, setPendingAction] =
    useState<RubricPendingAction | null>(null);
  const [toasts, setToasts] = useState<RubricToastData[]>([]);
  const [currentTab, setCurrentTab] = useState<'all' | RubricStatus>('all');

  const dismissToast = useCallback((toastId: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== toastId));
  }, []);

  const pushToast = useCallback((toast: RubricToastData) => {
    setToasts((prev) => [toast, ...prev]);
  }, []);

  useEffect(() => {
    if (toasts.length === 0) {
      return;
    }

    const newestToastId = toasts[0].id;
    const timeout = window.setTimeout(() => {
      dismissToast(newestToastId);
    }, 4500);

    return () => window.clearTimeout(timeout);
  }, [dismissToast, toasts]);

  const getRubricNameById = useCallback(
    (rubricId: string) => {
      const rubric = list.find((r) => r.id === rubricId);
      return rubric ? rubric.name : 'Rubric';
    },
    [list],
  );

  const isAlive = useAlive();
  return (
    <div className="bg-radial-top-soft">
      <MainLayout showBackToTop={false} scrollableRef={yScrollRef}>
        <Helmet>
          <title>{documentTitle(language.listRubricsPageTitle())}</title>
        </Helmet>

        <UserAnalysesOverlay onUserAnalysisCancel={() => {}} />
        <ObservationLoadingAndFailureIndicators
          isLoadingStill={isLoadingObservationStill}
          failedToLoadObservation={failedToLoadObservation}
          isDashboardDetailPage={true}
        />

        {!isLoadingObservationStill &&
          selectedOrganizationId &&
          selectedDatabaseId &&
          selectedDatabaseRevisionId && (
            <PageSection>
              <ListRubricsView
                canManageRubrics={canManageRubrics}
                list={list}
                unarchiveRubric={(rubricId) => {
                  setUnarchivingRubric(rubricId);

                  clientUpdateRubricStatus({
                    organizationId: selectedOrganizationId,
                    databaseId: selectedDatabaseId,
                    databaseRevisionId: selectedDatabaseRevisionId,
                    status: 'draft',
                    onError: () => {
                      pushToast(
                        makeToast({
                          action: 'unarchive',
                          name: getRubricNameById(rubricId),
                          type: 'error',
                        }),
                      );
                      setPendingAction(null);
                      setUnarchivingRubric(null);
                    },
                    onSuccess: () => {
                      setList((prev) =>
                        prev.map((item) =>
                          item.id === rubricId
                            ? { ...item, status: 'draft' }
                            : item,
                        ),
                      );
                      pushToast(
                        makeToast({
                          action: 'unarchive',
                          name: getRubricNameById(rubricId),
                          type: 'success',
                        }),
                      );
                      setPendingAction(null);
                      setUnarchivingRubric(null);
                    },
                    isAlive,
                    userAuthenticatedApiCall,
                    rubricId,
                  });
                }}
                deleteRubric={(rubricId) => {
                  setDeletingRubric(rubricId);

                  clientDeleteRubric({
                    organizationId: selectedOrganizationId,
                    databaseId: selectedDatabaseId,
                    databaseRevisionId: selectedDatabaseRevisionId,
                    onError: () => {
                      pushToast(
                        makeToast({
                          action: 'delete',
                          name: getRubricNameById(rubricId),
                          type: 'error',
                        }),
                      );
                      setPendingAction(null);
                      setDeletingRubric(null);
                    },
                    onSuccess: () => {
                      setList((prev) =>
                        prev.filter((item) => item.id !== rubricId),
                      );
                      pushToast(
                        makeToast({
                          action: 'delete',
                          name: getRubricNameById(rubricId),
                          type: 'success',
                        }),
                      );
                      setPendingAction(null);
                      setDeletingRubric(null);
                    },
                    isAlive,
                    userAuthenticatedApiCall,
                    rubricId,
                  });
                }}
                archiveRubric={(rubricId) => {
                  setArchivingRubric(rubricId);

                  clientUpdateRubricStatus({
                    organizationId: selectedOrganizationId,
                    databaseId: selectedDatabaseId,
                    databaseRevisionId: selectedDatabaseRevisionId,
                    status: 'archived',
                    onError: () => {
                      pushToast(
                        makeToast({
                          action: 'archive',
                          name: getRubricNameById(rubricId),
                          type: 'error',
                        }),
                      );
                      setPendingAction(null);
                      setArchivingRubric(null);
                    },
                    onSuccess: () => {
                      setList((prev) =>
                        prev.map((item) =>
                          item.id === rubricId
                            ? { ...item, status: 'archived' }
                            : item,
                        ),
                      );
                      pushToast(
                        makeToast({
                          action: 'archive',
                          name: getRubricNameById(rubricId),
                          type: 'success',
                        }),
                      );
                      setPendingAction(null);
                      setArchivingRubric(null);
                    },
                    isAlive,
                    userAuthenticatedApiCall,
                    rubricId,
                  });
                }}
                publishRubric={(rubricId) => {
                  setPublishingRubric(rubricId);

                  clientUpdateRubricStatus({
                    organizationId: selectedOrganizationId,
                    databaseId: selectedDatabaseId,
                    databaseRevisionId: selectedDatabaseRevisionId,
                    status: 'published',
                    onError: () => {
                      pushToast(
                        makeToast({
                          action: 'publish',
                          name: getRubricNameById(rubricId),
                          type: 'error',
                        }),
                      );
                      setPendingAction(null);
                      setPublishingRubric(null);
                    },
                    onSuccess: () => {
                      setList((prev) =>
                        prev.map((item) =>
                          item.id === rubricId
                            ? { ...item, status: 'published' }
                            : item,
                        ),
                      );
                      pushToast(
                        makeToast({
                          action: 'publish',
                          name: getRubricNameById(rubricId),
                          type: 'success',
                        }),
                      );
                      setPendingAction(null);
                      setPublishingRubric(null);
                    },
                    isAlive,
                    userAuthenticatedApiCall,
                    rubricId,
                  });
                }}
                unpublishRubric={(rubricId) => {
                  setUnpublishingRubric(rubricId);

                  clientUpdateRubricStatus({
                    organizationId: selectedOrganizationId,
                    databaseId: selectedDatabaseId,
                    databaseRevisionId: selectedDatabaseRevisionId,
                    status: 'draft',
                    onError: () => {
                      pushToast(
                        makeToast({
                          action: 'unpublish',
                          name: getRubricNameById(rubricId),
                          type: 'error',
                        }),
                      );
                      setPendingAction(null);
                      setUnpublishingRubric(null);
                    },
                    onSuccess: () => {
                      setList((prev) =>
                        prev.map((item) =>
                          item.id === rubricId
                            ? { ...item, status: 'draft' }
                            : item,
                        ),
                      );
                      pushToast(
                        makeToast({
                          action: 'unpublish',
                          name: getRubricNameById(rubricId),
                          type: 'success',
                        }),
                      );
                      setPendingAction(null);
                      setUnpublishingRubric(null);
                    },
                    isAlive,
                    userAuthenticatedApiCall,
                    rubricId,
                  });
                }}
                currentTab={currentTab}
                version={
                  isLoadingRubrics
                    ? 'loading'
                    : isFailedLoadingRubrics
                      ? 'error'
                      : list.length === 0
                        ? 'empty'
                        : 'loaded'
                }
                archivingRubric={archivingRubric}
                unarchivingRubric={unarchivingRubric}
                deletingRubric={deletingRubric}
                publishingRubric={publishingRubric}
                unpublishingRubric={unpublishingRubric}
                onTabClick={(newTab) => {
                  setCurrentTab(newTab);
                }}
                pendingAction={pendingAction}
                setPendingAction={setPendingAction}
                toasts={toasts}
                dismissToast={dismissToast}
              />
            </PageSection>
          )}
      </MainLayout>
    </div>
  );
};
